import { useState } from 'react';
import { Announcement } from '@core/interfaces/api';
import { format, parseISO, addDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { environment } from 'src/environments/environment';

export interface UseDeleteActivityOptions {
  siteId: number | null;
  timezone?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export function useDeleteActivity({
  siteId,
  timezone,
  onSuccess,
  onError,
}: UseDeleteActivityOptions) {
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteActivity = async (
    activity: Announcement,
    scope: 'single' | 'future' | 'all',
  ) => {
    if (!siteId || !timezone) {
      console.error('Missing siteId or timezone');
      onError?.(new Error('Missing siteId or timezone'));
      return;
    }

    try {
      setIsDeleting(true);

      if (!activity.recurrence) {
        // Simple non-recurring event delete
        await fetch(
          `${environment.apiv3Url}/announcements/${activity.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deleted: true }),
          },
        );
      } else if (scope === 'single') {
        // Delete single instance of recurring event
        // 1. Add date to exdates
        const parsedDate = parseISO(activity.eventStart as unknown as string);
        const zonedDate = utcToZonedTime(parsedDate, timezone);
        const startFromISO = format(zonedDate, 'yyyy-MM-dd');

        let exdates: string[] = [];
        if (activity.recurrence.exdates) {
          exdates.push(...activity.recurrence.exdates);
          exdates.push(startFromISO);
        } else {
          exdates = [startFromISO];
        }

        // 2. Update recurrence with new exception
        await fetch(
          `${environment.apiv3Url}/recurrence`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...activity.recurrence,
              exdates,
              byweekday: activity.recurrence.byweekday?.map((day) =>
                typeof day === 'number' ? String(day) : day,
              ),
            }),
          },
        );

        // 3. Delete this announcement
        await fetch(
          `${environment.apiv3Url}/announcements/${activity.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deleted: true }),
          },
        );
      } else if (scope === 'future') {
        // Delete this and future recurring events
        // 1. Update recurrence with new until date
        const parsedDate = parseISO(activity.eventStart as unknown as string);
        const zonedDate = utcToZonedTime(parsedDate, timezone);
        const endOfThePreviousDay = format(addDays(zonedDate, -1), 'yyyy-MM-dd');

        await fetch(
          `${environment.apiv3Url}/recurrence`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...activity.recurrence,
              count: null,
              until: endOfThePreviousDay,
              byweekday: activity.recurrence.byweekday?.map((day) =>
                typeof day === 'number' ? String(day) : day,
              ),
            }),
          },
        );

        // 2. Delete this and future announcements
        await fetch(
          `${environment.apiv3Url}/recurrence/announcements/${siteId}/recurrence/${activity.recurrence.id}/delete-future/${new Date(activity.eventStart).getTime()}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
          },
        );
      } else if (scope === 'all') {
        // Delete all events in the series
        // 1. Get all events with this recurrence ID
        const response = await fetch(
          `${environment.apiv3Url}/announcements/${siteId}/recurrence/${activity.recurrence.id}`,
        );
        const announcements: Announcement[] = await response.json();

        // 2. Delete all announcements and the recurrence
        await Promise.all([
          // Delete all announcements
          fetch(
            `${environment.apiv3Url}/announcements/bulk/delete`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(announcements.map((a) => ({ id: a.id }))),
            },
          ),
          // Delete recurrence
          fetch(
            `${environment.apiv3Url}/recurrence/${activity.recurrence.id}`,
            {
              method: 'DELETE',
            },
          ),
        ]);
      }

      onSuccess?.();
    } catch (error) {
      console.error('Error deleting activity:', error);
      onError?.(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteActivity,
    isDeleting,
  };
}
